import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  HStack,
  Image,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { useFinancialClientsByCategory, useFinancialPlansByCategory } from '@partiu/api/dashboard/hooks.ts';
import { DashboardTemplate } from '../../components/templates/DashboardTemplate.tsx';
import { categoryBadge } from './badges.ts';
import { FinancialPagination, financialErrorMessage } from './shared.tsx';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function formatUpdatedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, "dd/MM/yyyy 'às' HH:mm");
}

function CategoryMark({ name, rank }: { name: string; rank?: number }) {
  const src = categoryBadge(name, rank);
  if (!src) {
    return (
      <Flex boxSize="48px" borderRadius="full" bg="gray.100" align="center" justify="center" flexShrink={0}>
        <Text fontWeight="700" color="gray.500">
          {name.trim().slice(0, 1).toUpperCase() || '•'}
        </Text>
      </Flex>
    );
  }

  return <Image src={src} alt="" boxSize="48px" objectFit="contain" flexShrink={0} />;
}

function ClientsTab() {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, index) => currentYear - index);
  const [year, setYear] = useState<number | ''>(currentYear);
  const [month, setMonth] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching } = useFinancialClientsByCategory({
    page,
    year: year === '' ? undefined : year,
    month: year !== '' && month !== '' ? month : undefined,
  });
  const updatedAt = formatUpdatedAt(data?.rebuiltAt);
  const results = data?.results ?? [];

  return (
    <VStack align="stretch" spacing={4}>
      <HStack spacing={3} align="flex-end" flexWrap="wrap">
        <Box>
          <Text fontSize="xs" mb={1} color="gray.500" fontWeight="600">
            Ano
          </Text>
          <Select
            value={year === '' ? '' : String(year)}
            onChange={(event) => {
              const next = event.target.value;
              setYear(next === '' ? '' : Number(next));
              if (next === '') setMonth('');
              setPage(1);
            }}
            bg="white"
            borderRadius="lg"
            maxW="180px"
          >
            <option value="">Todo o período</option>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" mb={1} color="gray.500" fontWeight="600">
            Mês
          </Text>
          <Select
            value={month === '' ? '' : String(month)}
            isDisabled={year === ''}
            onChange={(event) => {
              const next = event.target.value;
              setMonth(next === '' ? '' : Number(next));
              setPage(1);
            }}
            bg="white"
            borderRadius="lg"
            maxW="200px"
          >
            <option value="">Todos</option>
            {MONTHS.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </Select>
        </Box>
        {updatedAt && (
          <Text fontSize="sm" color="gray.500" pb={2}>
            Atualizado em {updatedAt}
          </Text>
        )}
      </HStack>

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Text color="red.600">{financialErrorMessage(error)}</Text>
      ) : results.length === 0 ? (
        <Text color="gray.500">Nenhuma categoria encontrada.</Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {results.map((row) => (
            <HStack
              key={row.levelId}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="xl"
              px={4}
              py={3}
              justify="space-between"
              spacing={4}
            >
              <HStack spacing={4} minW={0}>
                <CategoryMark name={row.levelName} rank={row.levelRank} />
                <Text fontWeight="700" fontSize="md" noOfLines={2}>
                  {row.levelName}
                </Text>
              </HStack>
              <Box textAlign="right" flexShrink={0}>
                <Text fontSize="2xl" fontWeight="700" lineHeight="1">
                  {row.clientsCount.toLocaleString('pt-BR')}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {row.clientsCount === 1 ? 'cliente' : 'clientes'}
                </Text>
              </Box>
            </HStack>
          ))}
        </VStack>
      )}

      {!isError && (
        <FinancialPagination
          page={page}
          count={data?.count}
          hasPrevious={!!data?.previous}
          hasNext={!!data?.next}
          isLoading={isFetching}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          itemLabel={['categoria', 'categorias']}
        />
      )}
    </VStack>
  );
}

function PlansTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching } = useFinancialPlansByCategory(page);
  const results = data?.results ?? [];

  return (
    <VStack align="stretch" spacing={4}>
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Text color="red.600">{financialErrorMessage(error)}</Text>
      ) : results.length === 0 ? (
        <Text color="gray.500">Nenhuma categoria encontrada.</Text>
      ) : (
        <Accordion allowToggle display="flex" flexDirection="column" gap={3}>
            {results.map((row, index) => (
              <AccordionItem
                key={`${row.levelId}-${index}`}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                bg="white"
                overflow="hidden"
              >
                <AccordionButton px={4} py={3} _hover={{ bg: 'gray.50' }}>
                  <HStack flex="1" spacing={4} minW={0}>
                    <CategoryMark name={row.levelName} rank={row.levelRank} />
                    <Box textAlign="left" minW={0}>
                      <Text fontWeight="700" noOfLines={1}>
                        {row.levelName}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {row.plansCount.toLocaleString('pt-BR')} {row.plansCount === 1 ? 'plano' : 'planos'}
                      </Text>
                    </Box>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={4} pb={4} bg="gray.50">
                  {row.plans.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Nenhum plano nesta categoria.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto" pr={1}>
                      {row.plans.map((plan) => {
                        const sameCategory =
                          plan.planName.trim().toLowerCase() === row.levelName.trim().toLowerCase();
                        const planLabel = sameCategory ? '' : plan.planName;
                        const productLabel = plan.productId != null ? plan.productName?.trim() : '';
                        const subscriptions = plan.subscriptionsCount ?? 0;

                        return (
                          <HStack key={plan.planId} bg="white" borderRadius="lg" px={4} py={3} spacing={4} align="center">
                            <Box flex="1" minW={0}>
                              <Text fontWeight="600">
                                {planLabel || productLabel ? (
                                  <>
                                    {planLabel}
                                    {planLabel && productLabel ? ' · ' : ''}
                                    {productLabel}
                                  </>
                                ) : (
                                  plan.planSlugIntegration || '—'
                                )}
                              </Text>
                              {(planLabel || productLabel) && plan.planSlugIntegration && (
                                <Text fontSize="sm" color="gray.500">
                                  {plan.planSlugIntegration}
                                </Text>
                              )}
                            </Box>
                            <Box textAlign="right" flexShrink={0}>
                              <Text fontWeight="700">{subscriptions.toLocaleString('pt-BR')}</Text>
                              <Text fontSize="xs" color="gray.500">
                                {subscriptions === 1 ? 'assinatura' : 'assinaturas'}
                              </Text>
                            </Box>
                          </HStack>
                        );
                      })}
                    </VStack>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
        </Accordion>
      )}

      {!isError && (
        <FinancialPagination
          page={page}
          count={data?.count}
          hasPrevious={!!data?.previous}
          hasNext={!!data?.next}
          isLoading={isFetching}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          itemLabel={['categoria', 'categorias']}
        />
      )}
    </VStack>
  );
}

export function FinancialPage() {
  const { colors } = useTheme();
  const [tab, setTab] = useState<'clients' | 'plans'>('clients');
  const primary = colors.primaryColor || '#1a202c';

  return (
    <DashboardTemplate>
      <VStack align="stretch" spacing={5} w="100%" p={{ base: 4, md: 6 }} maxW="880px">
        <Box>
          <Heading as="h1" size="lg">
            Financeiro
          </Heading>
          <Text mt={1} color="gray.500">
            Clientes e planos agrupados por categoria.
          </Text>
        </Box>

        <HStack spacing={2} bg="gray.100" p="4px" borderRadius="full" w="fit-content">
          {(
            [
              { id: 'clients', label: 'Clientes por categoria' },
              { id: 'plans', label: 'Planos por categoria' },
            ] as const
          ).map((item) => {
            const selected = tab === item.id;
            return (
              <Box
                key={item.id}
                as="button"
                type="button"
                onClick={() => setTab(item.id)}
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="600"
                bg={selected ? 'white' : 'transparent'}
                color={selected ? primary : 'gray.600'}
                boxShadow={selected ? 'sm' : 'none'}
              >
                {item.label}
              </Box>
            );
          })}
        </HStack>

        {tab === 'clients' ? <ClientsTab /> : <PlansTab />}
      </VStack>
    </DashboardTemplate>
  );
}
